import type {
  AssessmentAggregation,
  AssessmentDirection,
  AssessmentValueType,
  AttemptStatus,
  CreateDefinitionRequest,
  CreateSessionRequest,
  DefinitionView,
  LocalizedTexts,
  NewDefinitionVersionRequest,
  ProgressChangeView,
  ProgressSeriesView,
  ResultBatchView,
  ResultRowInput,
  ResultView,
  SessionExportView,
  SessionProtocolView,
  SessionView,
  SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { csvTextCell } from './csv';
import { wholeNumber } from './scheduling';

export const valueTypeKeys: Readonly<Record<AssessmentValueType, TranslationKey>> = {
  numeric: 'assessments.valueType.numeric',
  duration: 'assessments.valueType.duration',
  boolean: 'assessments.valueType.boolean',
  rubric: 'assessments.valueType.rubric',
};

export const directionKeys: Readonly<Record<AssessmentDirection, TranslationKey>> = {
  lower_is_better: 'assessments.direction.lower_is_better',
  higher_is_better: 'assessments.direction.higher_is_better',
};

export const aggregationKeys: Readonly<Record<AssessmentAggregation, TranslationKey>> = {
  best: 'assessments.aggregation.best',
  mean: 'assessments.aggregation.mean',
  last: 'assessments.aggregation.last',
  coach_selected: 'assessments.aggregation.coach_selected',
};

export const attemptStatusKeys: Readonly<Record<AttemptStatus, TranslationKey>> = {
  valid: 'assessments.status.valid',
  invalid: 'assessments.status.invalid',
  not_attempted: 'assessments.status.not_attempted',
};

/** A club-authored text in the reader's language, falling back to English, then to any language. */
export function localizedText(
  texts: LocalizedTexts | null | undefined,
  locale: SupportedLocale,
): string {
  if (texts === null || texts === undefined) return '';
  return texts[locale] ?? texts.en ?? texts.hu ?? texts.ro ?? '';
}

/** The department choice that makes a protocol available to the whole club. */
export const wholeClub = 'club';

const decimalPattern = /^-?\d{1,15}(\.\d{1,15})?$/;

// ---------------------------------------------------------------------------------------------
// Protocol form
// ---------------------------------------------------------------------------------------------

export interface DefinitionForm {
  departmentId: string;
  code: string;
  title: string;
  instructions: string;
  valueType: AssessmentValueType;
  unit: string;
  precision: string;
  minimum: string;
  maximum: string;
  direction: AssessmentDirection;
  attemptsAllowed: string;
  aggregation: AssessmentAggregation;
  allowOutOfRange: boolean;
  ratioScale: boolean;
  /** One description per level, in the current language; levels are numbered from 1. */
  rubricLevels: string[];
  distance: string;
  course: string;
  surface: string;
  method: string;
}

export const emptyDefinitionForm = (departmentId: string): DefinitionForm => ({
  departmentId,
  code: '',
  title: '',
  instructions: '',
  valueType: 'duration',
  unit: 'seconds',
  precision: '2',
  minimum: '0',
  maximum: '',
  direction: 'lower_is_better',
  attemptsAllowed: '3',
  aggregation: 'best',
  allowOutOfRange: false,
  ratioScale: true,
  rubricLevels: [],
  distance: '',
  course: '',
  surface: '',
  method: '',
});

/** Changing the type resets the settings that only make sense for some types. */
export function withValueType(
  form: DefinitionForm,
  valueType: AssessmentValueType,
): DefinitionForm {
  switch (valueType) {
    case 'duration':
      return {
        ...form,
        valueType,
        unit: 'seconds',
        precision: form.precision === '0' ? '2' : form.precision,
        minimum: form.minimum === '' ? '0' : form.minimum,
        direction: 'lower_is_better',
        ratioScale: true,
        rubricLevels: [],
      };
    case 'numeric':
      return {
        ...form,
        valueType,
        unit: form.unit === 'seconds' ? '' : form.unit,
        ratioScale: false,
        rubricLevels: [],
      };
    case 'boolean':
      return {
        ...form,
        valueType,
        unit: '',
        precision: '0',
        minimum: '',
        maximum: '',
        direction: 'higher_is_better',
        aggregation: form.aggregation === 'mean' ? 'best' : form.aggregation,
        ratioScale: false,
        allowOutOfRange: false,
        rubricLevels: [],
      };
    case 'rubric':
      return {
        ...form,
        valueType,
        unit: '',
        precision: '0',
        minimum: '',
        maximum: '',
        direction: 'higher_is_better',
        aggregation: form.aggregation === 'mean' ? 'best' : form.aggregation,
        ratioScale: false,
        allowOutOfRange: false,
        rubricLevels: form.rubricLevels.length >= 2 ? form.rubricLevels : ['', ''],
      };
  }
}

export function definitionFormOf(
  definition: DefinitionView,
  locale: SupportedLocale,
): DefinitionForm {
  return {
    departmentId: definition.departmentId ?? wholeClub,
    code: definition.code,
    title: localizedText(definition.titles, locale),
    instructions: localizedText(definition.instructions, locale),
    valueType: definition.valueType,
    unit: definition.unit ?? '',
    precision: String(definition.precision),
    minimum: definition.minimum ?? '',
    maximum: definition.maximum ?? '',
    direction: definition.direction,
    attemptsAllowed: String(definition.attemptsAllowed),
    aggregation: definition.aggregation,
    allowOutOfRange: definition.allowOutOfRange,
    ratioScale: definition.ratioScale,
    rubricLevels: (definition.rubricLevels ?? []).map((level) =>
      localizedText(level.descriptions, locale),
    ),
    distance: definition.protocol.distance ?? '',
    course: definition.protocol.course ?? '',
    surface: definition.protocol.surface ?? '',
    method: definition.protocol.method ?? '',
  };
}

const codePattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Basic completeness only; the server applies the full rules and explains what it refuses. */
export function canSubmitDefinition(form: DefinitionForm, isNew: boolean): boolean {
  if (isNew && (form.departmentId === '' || !codePattern.test(form.code.trim().toLowerCase()))) {
    return false;
  }
  if (form.title.trim() === '') return false;
  const precision = wholeNumber(form.precision);
  const attempts = wholeNumber(form.attemptsAllowed);
  if (precision === null || precision > 4) return false;
  if (attempts === null || attempts < 1 || attempts > 20) return false;
  for (const bound of [form.minimum, form.maximum]) {
    if (bound.trim() !== '' && !decimalPattern.test(bound.trim())) return false;
  }
  if (form.valueType === 'numeric' && form.unit.trim() === '') return false;
  if (form.valueType === 'rubric') {
    const described = form.rubricLevels.filter((level) => level.trim() !== '');
    if (form.rubricLevels.length < 2 || described.length !== form.rubricLevels.length) return false;
  }
  return true;
}

const supportedLanguages = ['en', 'hu', 'ro'] as const;

function mergedTexts(
  existing: LocalizedTexts | null | undefined,
  locale: SupportedLocale,
  text: string,
): LocalizedTexts {
  const merged: LocalizedTexts = {};
  for (const language of supportedLanguages) {
    const value = language === locale ? text.trim() : existing?.[language];
    if (value !== undefined && value !== '') merged[language] = value;
  }
  return merged;
}

export interface DefinitionBase {
  titles?: DefinitionView['titles'];
  instructions?: LocalizedTexts | null;
  rubricLevels?: DefinitionView['rubricLevels'];
}

/**
 * The text is stored under the language the author is writing in; a new version keeps what was
 * written in the other languages, so translating a protocol never loses an earlier translation.
 */
export function definitionVersionBody(
  form: DefinitionForm,
  locale: SupportedLocale,
  base: DefinitionBase = {},
): NewDefinitionVersionRequest {
  const measured = form.valueType === 'numeric' || form.valueType === 'duration';
  const instructions = mergedTexts(base.instructions, locale, form.instructions);
  const optional = (text: string): string | null => (text.trim() === '' ? null : text.trim());
  return {
    titles: { ...base.titles, [locale]: form.title.trim() },
    instructions: Object.keys(instructions).length === 0 ? null : instructions,
    valueType: form.valueType,
    unit: form.valueType === 'duration' ? 'seconds' : measured ? optional(form.unit) : null,
    precision: wholeNumber(form.precision) ?? 0,
    minimum: measured ? optional(form.minimum) : null,
    maximum: measured ? optional(form.maximum) : null,
    direction: form.direction,
    attemptsAllowed: wholeNumber(form.attemptsAllowed) ?? 1,
    aggregation: form.aggregation,
    allowOutOfRange: measured && form.allowOutOfRange,
    ratioScale: measured && form.ratioScale,
    rubricLevels:
      form.valueType === 'rubric'
        ? form.rubricLevels.map((description, index) => ({
            descriptions: mergedTexts(
              base.rubricLevels?.[index]?.descriptions,
              locale,
              description,
            ),
          }))
        : null,
    protocol: {
      distance: optional(form.distance),
      course: optional(form.course),
      surface: optional(form.surface),
      method: optional(form.method),
    },
  };
}

/** A new protocol: the version content plus the department it belongs to and its stable key. */
export function definitionRequestBody(
  form: DefinitionForm,
  locale: SupportedLocale,
): CreateDefinitionRequest {
  return {
    departmentId:
      form.departmentId === '' || form.departmentId === wholeClub ? null : form.departmentId,
    code: form.code.trim().toLowerCase(),
    ...definitionVersionBody(form, locale),
  };
}

// ---------------------------------------------------------------------------------------------
// Session form
// ---------------------------------------------------------------------------------------------

export interface SessionForm {
  departmentId: string;
  sessionDate: string;
  context: string;
  definitionVersionIds: string[];
  athleteIds: string[];
}

export const emptySessionForm = (departmentId: string, today: string): SessionForm => ({
  departmentId,
  sessionDate: today,
  context: '',
  definitionVersionIds: [],
  athleteIds: [],
});

export function toggled(selected: readonly string[], identifier: string): string[] {
  return selected.includes(identifier)
    ? selected.filter((candidate) => candidate !== identifier)
    : [...selected, identifier];
}

/** Adds identifiers to a selection once each, keeping the order already chosen. */
export function withAdditions(selected: readonly string[], additions: readonly string[]): string[] {
  const result = [...selected];
  for (const identifier of additions) {
    if (!result.includes(identifier)) result.push(identifier);
  }
  return result;
}

export function canSubmitSession(form: SessionForm): boolean {
  return (
    form.departmentId !== '' &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.sessionDate) &&
    form.definitionVersionIds.length >= 1 &&
    form.definitionVersionIds.length <= 20 &&
    form.athleteIds.length >= 1 &&
    form.athleteIds.length <= 200
  );
}

export function sessionRequestBody(form: SessionForm): CreateSessionRequest {
  return {
    departmentId: form.departmentId,
    sessionDate: form.sessionDate,
    context: form.context.trim() === '' ? null : form.context.trim(),
    eventId: null,
    definitionVersionIds: form.definitionVersionIds,
    athleteIds: form.athleteIds,
  };
}

// ---------------------------------------------------------------------------------------------
// Result entry
// ---------------------------------------------------------------------------------------------

export interface AttemptCell {
  /** Decimal text; `1` or `0` for a yes/no result, the level number for a rubric. Blank: not made. */
  value: string;
  /** The attempt was made but spoiled; it has no value and never counts. */
  invalid: boolean;
}

export interface EntryRow {
  athleteId: string;
  definitionVersionId: string;
  /** The row version the coach saw; zero while no result exists. */
  version: number;
  cells: AttemptCell[];
  selectedAttempt: string;
  selectionReason: string;
  privateNote: string;
  publicComment: string;
  /** A result already exists (a draft or a published one). */
  existing: boolean;
  /** The stored result is published and has no draft: only a correction changes it. */
  published: boolean;
  /** Another coach changed the row first; the stored entry is shown. */
  conflict: boolean;
  /** Translation keys explaining why the last save refused this row. */
  problems: string[];
  /** The content as last loaded or saved, to tell what changed. */
  baseline: string;
}

function contentKey(
  row: Pick<
    EntryRow,
    'cells' | 'selectedAttempt' | 'selectionReason' | 'privateNote' | 'publicComment'
  >,
): string {
  return JSON.stringify([
    row.cells.map((cell) => [cell.value.trim(), cell.invalid]),
    row.selectedAttempt.trim(),
    row.selectionReason.trim(),
    row.privateNote.trim(),
    row.publicComment.trim(),
  ]);
}

/** The row for an athlete and protocol, from the stored result (its draft first) or blank. */
export function rowFromResult(
  athleteId: string,
  protocol: Pick<SessionProtocolView, 'definitionVersionId' | 'attemptsAllowed'>,
  result: ResultView | undefined,
): EntryRow {
  const revision = result?.draft ?? result?.published ?? null;
  const cells: AttemptCell[] = Array.from({ length: protocol.attemptsAllowed }, () => ({
    value: '',
    invalid: false,
  }));
  for (const attempt of revision?.attempts ?? []) {
    const cell = cells[attempt.attemptNumber - 1];
    if (cell === undefined) continue;
    if (attempt.status === 'valid') cell.value = attempt.value ?? '';
    else if (attempt.status === 'invalid') cell.invalid = true;
  }
  const row = {
    athleteId,
    definitionVersionId: protocol.definitionVersionId,
    version: result?.version ?? 0,
    cells,
    selectedAttempt:
      revision?.selectedAttemptNumber === null || revision === null
        ? ''
        : String(revision.selectedAttemptNumber),
    selectionReason: revision?.selectionReason ?? '',
    privateNote: revision?.privateNote ?? '',
    publicComment: revision?.publicComment ?? '',
    existing: result !== undefined,
    published: result?.draft === null && result.published !== null,
    conflict: false,
    problems: [] as string[],
  };
  return { ...row, baseline: contentKey(row) };
}

export function entryRowsFor(session: SessionView, protocol: SessionProtocolView): EntryRow[] {
  return session.participants.map((participant) =>
    rowFromResult(
      participant.athleteId,
      protocol,
      session.results.find(
        (result) =>
          result.athleteId === participant.athleteId &&
          result.definitionVersionId === protocol.definitionVersionId,
      ),
    ),
  );
}

export function isDirty(row: EntryRow): boolean {
  return contentKey(row) !== row.baseline;
}

export interface AttemptPayload {
  status: AttemptStatus;
  value: string | null;
  note: null;
}

/**
 * The attempts to send. Trailing attempts that were never made are left out; one in the middle is
 * sent as not attempted. A row with nothing entered sends a single not-attempted try only when a
 * result already exists, so an untouched row stores nothing.
 */
export function attemptsOf(row: EntryRow): AttemptPayload[] {
  let last = -1;
  row.cells.forEach((cell, index) => {
    if (cell.invalid || cell.value.trim() !== '') last = index;
  });
  if (last < 0) {
    return row.existing ? [{ status: 'not_attempted', value: null, note: null }] : [];
  }
  return row.cells.slice(0, last + 1).map((cell): AttemptPayload => {
    if (cell.invalid) return { status: 'invalid', value: null, note: null };
    if (cell.value.trim() === '') return { status: 'not_attempted', value: null, note: null };
    return { status: 'valid', value: cell.value.trim(), note: null };
  });
}

/** Rows that changed, can still be edited, and have something to save. */
export function changedRows(rows: readonly EntryRow[]): EntryRow[] {
  return rows.filter((row) => !row.published && isDirty(row) && attemptsOf(row).length > 0);
}

export function pendingEntryCount(rows: readonly EntryRow[]): number {
  return changedRows(rows).length;
}

const optionalText = (text: string): string | null => (text.trim() === '' ? null : text.trim());

export function resultItemOf(row: EntryRow): ResultRowInput {
  const selected = wholeNumber(row.selectedAttempt);
  return {
    athleteId: row.athleteId,
    definitionVersionId: row.definitionVersionId,
    version: row.version,
    unit: null,
    attempts: attemptsOf(row),
    selectedAttemptNumber: selected,
    selectionReason: optionalText(row.selectionReason),
    privateNote: optionalText(row.privateNote),
    publicComment: optionalText(row.publicComment),
  };
}

/**
 * Merges the answer to a save back into the rows. A saved row takes its new version; a conflict
 * shows the other coach's stored entry instead of overwriting it; a refused row keeps what the
 * coach typed together with the reasons; a published row says a correction is needed.
 */
export function applyOutcomes(
  rows: readonly EntryRow[],
  batch: ResultBatchView,
  protocol: Pick<SessionProtocolView, 'definitionVersionId' | 'attemptsAllowed'>,
): EntryRow[] {
  return rows.map((row) => {
    const outcome = batch.results.find(
      (candidate) =>
        candidate.athleteId === row.athleteId &&
        candidate.definitionVersionId === row.definitionVersionId,
    );
    if (outcome === undefined) return row;
    switch (outcome.outcome) {
      case 'saved':
        return rowFromResult(row.athleteId, protocol, outcome.result ?? undefined);
      case 'conflict':
        return {
          ...rowFromResult(row.athleteId, protocol, outcome.result ?? undefined),
          conflict: true,
        };
      case 'published':
        return {
          ...rowFromResult(row.athleteId, protocol, outcome.result ?? undefined),
          problems: ['error.assessment.resultPublished'],
        };
      case 'not_found':
        return { ...row, problems: ['error.notFound'] };
      case 'rejected':
        return {
          ...row,
          conflict: false,
          problems: outcome.problems.map((problem) => problem.messageKey),
        };
    }
  });
}

// ---------------------------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------------------------

/** A recorded value as the family should read it: with its unit, as yes/no, or as a rubric level. */
export function formatSeriesValue(
  value: string | null,
  series: Pick<ProgressSeriesView, 'valueType' | 'unit' | 'rubricLevels'>,
  locale: SupportedLocale,
): string {
  if (value === null) return translate(locale, 'assessments.progress.noValue');
  if (series.valueType === 'boolean') {
    return translate(locale, value === '1' ? 'assessments.entry.yes' : 'assessments.entry.no');
  }
  if (series.valueType === 'rubric') {
    const level = series.rubricLevels?.find((candidate) => String(candidate.level) === value);
    const description = level === undefined ? '' : localizedText(level.descriptions, locale);
    return description === '' ? value : `${value} – ${description}`;
  }
  return series.unit === null ? value : `${value} ${series.unit}`;
}

export function changeLabel(change: ProgressChangeView, unit: string | null): string {
  const difference = change.absolute.startsWith('-') ? change.absolute : `+${change.absolute}`;
  return unit === null ? difference : `${difference} ${unit}`;
}

export interface ChartPoint {
  /** Unique per published result, so two sessions on one day stay distinct. */
  key: string;
  horizontal: number;
  vertical: number;
  date: string;
  value: string;
}

export interface ChartModel {
  width: number;
  height: number;
  /** Runs of consecutive valid points: a gap (no valid value) always ends a run, never reads as zero. */
  segments: ChartPoint[][];
  /** Dates with no valid value, to mark the gaps. */
  gaps: { key: string; horizontal: number; date: string }[];
  yMinimum: number;
  yMaximum: number;
}

const chartPadding = { left: 48, right: 16, top: 12, bottom: 28 };

function timestampOf(isoDate: string): number {
  return Date.parse(`${isoDate}T00:00:00.000Z`);
}

/**
 * Geometry of one series. Points are placed by date and joined only inside a series, so a changed
 * protocol (a different series) is never connected to this one, and absent values break the line.
 */
export function buildChart(
  series: Pick<ProgressSeriesView, 'points'>,
  width = 560,
  height = 220,
): ChartModel {
  const plotWidth = width - chartPadding.left - chartPadding.right;
  const plotHeight = height - chartPadding.top - chartPadding.bottom;
  const dates = series.points.map((point) => timestampOf(point.sessionDate));
  const earliest = Math.min(...dates);
  const latest = Math.max(...dates);
  const numbers = series.points.flatMap((point) =>
    point.value === null ? [] : [Number(point.value)],
  );
  let yMinimum = numbers.length === 0 ? 0 : Math.min(...numbers);
  let yMaximum = numbers.length === 0 ? 1 : Math.max(...numbers);
  if (yMinimum === yMaximum) {
    yMinimum -= 1;
    yMaximum += 1;
  } else {
    const margin = (yMaximum - yMinimum) * 0.1;
    yMinimum -= margin;
    yMaximum += margin;
  }
  // Counts and durations never go below zero, so the axis does not either.
  if (numbers.length > 0 && Math.min(...numbers) >= 0 && yMinimum < 0) yMinimum = 0;
  const horizontalOf = (timestamp: number): number =>
    chartPadding.left +
    (latest === earliest
      ? plotWidth / 2
      : ((timestamp - earliest) / (latest - earliest)) * plotWidth);
  const verticalOf = (value: number): number =>
    chartPadding.top + (1 - (value - yMinimum) / (yMaximum - yMinimum)) * plotHeight;
  const segments: ChartPoint[][] = [];
  const gaps: { key: string; horizontal: number; date: string }[] = [];
  let current: ChartPoint[] = [];
  for (const point of series.points) {
    const key = `${point.sessionId}-${String(point.revisionNumber)}`;
    const horizontal = horizontalOf(timestampOf(point.sessionDate));
    if (point.value === null) {
      gaps.push({ key, horizontal, date: point.sessionDate });
      if (current.length > 0) segments.push(current);
      current = [];
      continue;
    }
    current.push({
      key,
      horizontal,
      vertical: verticalOf(Number(point.value)),
      date: point.sessionDate,
      value: point.value,
    });
  }
  if (current.length > 0) segments.push(current);
  return { width, height, segments, gaps, yMinimum, yMaximum };
}

/** A measured value or number: already validated as a decimal, so it is written as it is. */
function csvValueCell(value: string | null): string {
  return value ?? '';
}

/**
 * The published results of a session as CSV in the reader's language, with a byte-order mark so
 * spreadsheets read the accents correctly. Decimal values keep a point whatever the language.
 */
export function sessionExportCsv(exported: SessionExportView, locale: SupportedLocale): string {
  const header = [
    'assessments.export.column.familyName',
    'assessments.export.column.givenName',
    'assessments.export.column.protocol',
    'assessments.export.column.protocolVersion',
    'assessments.export.column.unit',
    'assessments.export.column.status',
    'assessments.export.column.value',
    'assessments.export.column.revision',
    'assessments.export.column.publishedAt',
    'assessments.export.column.comment',
  ] as const satisfies readonly TranslationKey[];
  const lines = [header.map((key) => csvTextCell(translate(locale, key))).join(',')];
  for (const row of exported.rows) {
    lines.push(
      [
        csvTextCell(row.familyName),
        csvTextCell(row.givenName),
        csvTextCell(localizedText(row.titles, locale)),
        String(row.protocolVersion),
        csvTextCell(row.unit ?? ''),
        csvTextCell(translate(locale, `assessments.status.${row.status}`)),
        csvValueCell(row.value),
        String(row.revisionNumber),
        row.publishedAt ?? '',
        csvTextCell(row.publicComment ?? ''),
      ].join(','),
    );
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
